/**
 * Balance.jsx - Balance Générale SYSCOHADA
 * ═══════════════════════════════════════════════════════════════════
 *
 * Colonnes officielles d'une balance à colonnes multiples :
 *
 *  N° Cpte │ Libellé │ Mvt Débit │ Mvt Crédit │ Solde D │ Solde C
 *
 * Règles affichées :
 *  ✓ Σ Mouvements Débit   = Σ Mouvements Crédit
 *  ✓ Σ Soldes Débiteurs   = Σ Soldes Créditeurs
 *
 * Filtres : Classe | Recherche | Nature du solde
 * Theme   : fond blanc - accent #008fa0
 */

import React, { useEffect, useState, useMemo } from 'react';
import { useGetBalanceQuery } from 'actions/comptabilite/queries';
import { exportBalanceExcel, exportBalance6Excel, exportBalancePdf, comparerComptes } from './utils/exportComptaExcel';
//import PeriodFilter from 'components/PeriodFilter/PeriodFilter';
import {
  exportButtonGroupStyle,
  exportExcelButtonStyle,
  exportPdfButtonStyle,
} from './utils/exportButtonStyles';
import TableLoader from 'components/Loader/TableLoader';

// ── Google Fonts ─────────────────────────────────────────────────────
const injectFonts = () => {
  if (document.getElementById('bal-fonts')) return;
  const l = document.createElement('link');
  l.id   = 'bal-fonts';
  l.rel  = 'stylesheet';
  l.href = 'https://fonts.googleapis.com/css2?family=Playfair+Display:wght@700;900&family=DM+Mono:wght@400;500&family=DM+Sans:wght@400;500;600&display=swap';
  document.head.appendChild(l);
};

// ── Tokens ───────────────────────────────────────────────────────────
const C = {
  bg:        '#f0f5f6',
  surface:   '#ffffff',
  border:    '#c8d8db',
  borderSoft:'#e2eced',
  teal:      '#008fa0',
  tealDark:  '#006b78',
  tealLight: '#00b4ca',
  tealDim:   'rgba(0,143,160,0.08)',
  tealMid:   'rgba(0,143,160,0.15)',
  green:     '#1a7a52',
  greenDim:  'rgba(26,122,82,0.10)',
  red:       '#c93535',
  redDim:    'rgba(201,53,53,0.10)',
  amber:     '#b45309',
  amberDim:  'rgba(180,83,9,0.09)',
  text:      '#0d2226',
  textDim:   '#3d6068',
  textMuted: '#7da5ad',
  white:     '#ffffff',
  row0:      '#ffffff',
  row1:      '#f7fbfc',
  mono:      '"DM Mono","Courier New",monospace',
  display:   '"Playfair Display",Georgia,serif',
  sans:      '"DM Sans",system-ui,sans-serif',
};

// ── Formatage ────────────────────────────────────────────────────────
const fmt = (n) => {
  if (!n || n === 0) return '-';
  return new Intl.NumberFormat('fr-FR', { minimumFractionDigits: 0 }).format(n);
};

// ── normalizeBalance ─────────────────────────────────────────────────
function normalizeBalance(raw) {
  const empty = { balance: [], total_debits: 0, total_credits: 0, total_debiteur: 0, total_crediteur: 0, equilibre: true, nombre_comptes: 0 };
  if (!raw) return empty;
  return {
    balance:         Array.isArray(raw.balance) ? raw.balance : [],
    total_debits:    raw.total_debits    ?? 0,
    total_credits:   raw.total_credits   ?? 0,
    total_debiteur:  raw.total_debiteur  ?? 0,
    total_crediteur: raw.total_crediteur ?? 0,
    equilibre:       raw.equilibre       ?? true,
    nombre_comptes:  raw.nombre_comptes  ?? (Array.isArray(raw.balance) ? raw.balance.length : 0),
  };
}

// ── Styles ───────────────────────────────────────────────────────────
const thBase = {
  fontFamily:      C.mono,
  fontWeight:      600,
  letterSpacing:   '1px',
  textTransform:   'uppercase',
  backgroundColor: C.teal,
  padding:         '10px 10px',
  textAlign:       'right',
  borderBottom:    '2px solid ' + C.tealDark,
  borderRight:     '1px solid rgba(255,255,255,0.15)',
  whiteSpace:      'nowrap',
  verticalAlign:   'middle',
  color: '#000',
};

// En-têtes de groupe (Mouvements, Soldes)
const thGroup = {
  ...thBase,
  fontSize:    '10px',
  textAlign:   'center',
  borderBottom:'1px solid rgba(255,255,255,0.2)',
  borderRight: '2px solid rgba(255,255,255,0.3)',
};

// En-têtes de sous-colonne D / C
const thSub = {
  ...thBase,
  fontSize:  '10px',
  textAlign: 'center',
  padding:   '8px 8px',
};

// En-tête texte (N° Compte, Libellé)
const thLeft = {
  ...thBase,
  fontSize:  '10px',
  textAlign: 'left',
  padding:   '10px 12px',
};

const S = {
  wrapper: { fontFamily:C.sans, background:C.bg, color:C.text, padding:'28px 20px 60px', minHeight:'100vh' },
  inner:   { maxWidth:'1280px', margin:'0 auto' },

  headerRow: { display:'flex', alignItems:'center', gap:'14px', marginBottom:'6px' },
  title:     { fontFamily:C.display, fontSize:'1.6rem', fontWeight:700, color:C.text, margin:0 },
  badge: {
    fontFamily:C.mono, fontSize:'9px', letterSpacing:'2px', textTransform:'uppercase',
    color:C.teal, background:C.tealDim, border:'1px solid rgba(0,143,160,0.25)',
    padding:'3px 12px', borderRadius:'100px',
  },
  divider: { height:'1px', background:'linear-gradient(90deg,'+C.border+',transparent)', marginTop:'8px', marginBottom:'18px' },

  // Indicateurs équilibre en ligne
  indicators: { display:'flex', gap:'12px', flexWrap:'wrap', marginBottom:'20px' },
  indicator: (ok) => ({
    display:'inline-flex', alignItems:'center', gap:'6px',
    fontFamily:C.mono, fontSize:'11px', letterSpacing:'1px',
    color:      ok ? C.green : C.red,
    background: ok ? C.greenDim : C.redDim,
    border:     '1px solid ' + (ok ? 'rgba(26,122,82,0.3)' : 'rgba(201,53,53,0.3)'),
    padding:'5px 14px', borderRadius:'100px',
  }),

  // Filtres
  filterBar: {
    display:'flex', flexWrap:'wrap', gap:'10px', alignItems:'center',
    background:C.surface, border:'1px solid ' + C.border,
    borderRadius:'10px', padding:'14px 18px', marginBottom:'20px',
    boxShadow:'0 1px 6px rgba(0,143,160,0.06)',
  },
  filterLabel: { fontFamily:C.mono, fontSize:'9px', letterSpacing:'2px', textTransform:'uppercase', color:C.textMuted },
  filterInput: {
    fontFamily:C.sans, fontSize:'13px', color:C.text, background:C.bg,
    border:'1px solid ' + C.border, borderRadius:'6px', padding:'6px 10px', outline:'none',
  },
  filterSelect: {
    fontFamily:C.sans, fontSize:'12px', color:C.text, background:C.bg,
    border:'1px solid ' + C.border, borderRadius:'6px', padding:'6px 10px', cursor:'pointer', outline:'none',
  },
  resetBtn: {
    fontFamily:C.mono, fontSize:'10px', letterSpacing:'1px', textTransform:'uppercase',
    color:C.teal, background:'transparent', border:'1px solid rgba(0,143,160,0.3)',
    borderRadius:'6px', padding:'6px 12px', cursor:'pointer', marginLeft:'auto',
  },
  countBadge: {
    fontFamily:C.mono, fontSize:'10px', color:C.textMuted,
    background:C.tealDim, border:'1px solid rgba(0,143,160,0.2)',
    padding:'3px 10px', borderRadius:'100px',
  },

  // Tableau
  card: {
    background:C.surface, border:'1px solid ' + C.border,
    borderRadius:'10px', overflow:'hidden',
    boxShadow:'0 2px 20px rgba(0,143,160,0.08)',
  },
  tableWrap: {
    overflowX:'auto',
    WebkitOverflowScrolling:'touch',
  },
  table: { width:'100%', minWidth:'980px', borderCollapse:'collapse', fontSize:'12.5px' },

  TD: (extra={}) => ({
    padding:'9px 10px', verticalAlign:'middle',
    borderBottom:'1px solid ' + C.borderSoft,
    fontFamily:C.mono, fontSize:'12px', textAlign:'right',
    ...extra,
  }),

  // Colonne numéro compte
  tdCode: {
    padding:'9px 12px', verticalAlign:'middle',
    borderBottom:'1px solid ' + C.borderSoft,
    fontFamily:C.mono, fontSize:'12px', fontWeight:600,
    color:C.teal, textAlign:'left', whiteSpace:'nowrap',
    borderRight:'1px solid ' + C.borderSoft,
  },
  // Colonne libellé
  tdLib: {
    padding:'9px 12px', verticalAlign:'middle',
    borderBottom:'1px solid ' + C.borderSoft,
    fontFamily:C.sans, fontSize:'12.5px', color:C.text,
    textAlign:'left', borderRight:'2px solid ' + C.border,
    maxWidth:'220px',
  },
  // Séparateur de groupe
  tdGroupSep: { borderRight:'2px solid ' + C.border },

  amtD:    { color:C.green, fontWeight:600, fontFamily:C.mono, fontSize:'12px' },
  amtC:    { color:C.red,   fontWeight:600, fontFamily:C.mono, fontSize:'12px' },
  amtSolD: { color:C.green, fontWeight:700, fontFamily:C.mono, fontSize:'12px' },
  amtSolC: { color:C.red,   fontWeight:700, fontFamily:C.mono, fontSize:'12px' },
  amtNil:  { color:C.textMuted, fontFamily:C.mono, fontSize:'11px' },

  // Badge nature solde
  badgeD: { display:'inline-block', fontFamily:C.mono, fontSize:'9px', letterSpacing:'1px', color:C.green, background:C.greenDim, border:'1px solid rgba(26,122,82,0.3)', padding:'1px 7px', borderRadius:'4px' },
  badgeC: { display:'inline-block', fontFamily:C.mono, fontSize:'9px', letterSpacing:'1px', color:C.red,   background:C.redDim,   border:'1px solid rgba(201,53,53,0.3)', padding:'1px 7px', borderRadius:'4px' },
  badgeN: { display:'inline-block', fontFamily:C.mono, fontSize:'9px', letterSpacing:'1px', color:C.textMuted, background:C.tealDim, border:'1px solid '+C.border, padding:'1px 7px', borderRadius:'4px' },

  // Ligne de classe (regroupement)
  classeRow: { backgroundColor:'rgba(0,143,160,0.05)', borderTop:'1px solid rgba(0,143,160,0.15)' },
  classeTd: { padding:'6px 12px', fontFamily:C.mono, fontSize:'9px', letterSpacing:'2px', textTransform:'uppercase', color:C.teal, fontWeight:600 },

  // Ligne de total
  totalRow: { backgroundColor:'rgba(0,143,160,0.08)', borderTop:'3px solid ' + C.teal },
  totalTd: (extra={}) => ({
    padding:'11px 10px', fontFamily:C.mono, fontSize:'13px', fontWeight:700,
    color:C.teal, textAlign:'right', ...extra,
  }),

  loader: { textAlign:'center', padding:'60px', fontFamily:C.mono, fontSize:'12px', color:C.textMuted, letterSpacing:'2px' },
  empty:  { textAlign:'center', padding:'40px', fontFamily:C.mono, fontSize:'12px', color:C.textMuted, letterSpacing:'1px' },
};

// ── Labels de classe SYSCOHADA ────────────────────────────────────────
const CLASSE_LABELS = {
  '1': 'Classe 1 - Ressources durables',
  '2': 'Classe 2 - Actifs immobilisés',
  '3': 'Classe 3 - Stocks',
  '4': 'Classe 4 - Tiers',
  '5': 'Classe 5 - Trésorerie',
  '6': 'Classe 6 - Charges',
  '7': 'Classe 7 - Produits',
  '8': 'Classe 8 - Comptes spéciaux',
};

// ── Ligne de compte ───────────────────────────────────────────────────
function LigneCompte({ ecriture, index }) {
  const [hov, setHov] = useState(false);
  const nature = ecriture.nature_solde ?? (
    ecriture.solde_debiteur > 0 ? 'D' : ecriture.solde_crediteur > 0 ? 'C' : 'NUL'
  );

  const bg = hov ? 'rgba(0,143,160,0.04)' : (index % 2 === 0 ? C.row0 : C.row1);

  return (
    <tr
      style={{ background:bg, transition:'background .15s' }}
      onMouseEnter={() => setHov(true)}
      onMouseLeave={() => setHov(false)}
    >
      {/* N° Compte */}
      <td style={S.tdCode}>{ecriture.numero_comptable}</td>

      {/* Libellé */}
      <td style={S.tdLib}>
        <span style={{ fontSize:'12.5px' }}>{ecriture.libelle || '-'}</span>
      </td>

      {/* Mouvements - Débit */}
      <td style={S.TD()}>
        {ecriture.debit > 0
          ? <span style={S.amtD}>{fmt(ecriture.debit)}</span>
          : <span style={S.amtNil}>-</span>
        }
      </td>

      {/* Mouvements - Crédit */}
      <td style={S.TD(S.tdGroupSep)}>
        {ecriture.credit > 0
          ? <span style={S.amtC}>{fmt(ecriture.credit)}</span>
          : <span style={S.amtNil}>-</span>
        }
      </td>

      {/* Solde Final - Débiteur */}
      <td style={S.TD()}>
        {ecriture.solde_debiteur > 0
          ? <span style={S.amtSolD}>{fmt(ecriture.solde_debiteur)}</span>
          : <span style={S.amtNil}>-</span>
        }
      </td>

      {/* Solde Final - Créditeur */}
      <td style={S.TD(S.tdGroupSep)}>
        {ecriture.solde_crediteur > 0
          ? <span style={S.amtSolC}>{fmt(ecriture.solde_crediteur)}</span>
          : <span style={S.amtNil}>-</span>
        }
      </td>

      {/* Nature */}
      <td style={S.TD({ textAlign:'center' })}>
        {nature === 'D' && <span style={S.badgeD}>D</span>}
        {nature === 'C' && <span style={S.badgeC}>C</span>}
        {nature === 'NUL' && <span style={S.badgeN}>NUL</span>}
      </td>
    </tr>
  );
}

// ── Composant principal ───────────────────────────────────────────────
const Balance = ({ dossier_id, date_debut, date_fin }) => {
  useEffect(() => { injectFonts(); }, []);

  const [search,       setSearch]       = useState('');
  const [filterClasse, setFilterClasse] = useState('');
  const [filterSolde,  setFilterSolde]  = useState('');
  const [groupParClasse, setGroupParClasse] = useState(true);

  const { data: raw, isLoading, isFetching, refetch } = useGetBalanceQuery(
    { dossier_id, date_debut, date_fin },
    { skip: !dossier_id }
  );

  useEffect(() => { if (dossier_id) refetch(); }, [dossier_id, date_debut, date_fin, refetch]);
  useEffect(() => {
    const handleRefresh = () => refetch();
    window.addEventListener("compta:refresh", handleRefresh);
    return () => window.removeEventListener("compta:refresh", handleRefresh);
  }, [refetch]);

  const { balance, total_debits, total_credits, total_debiteur, total_crediteur, equilibre, nombre_comptes } =
    useMemo(() => normalizeBalance(raw), [raw]);
  const handleExportExcel = () => {
    exportBalanceExcel({
      balance,
      totalDebits: total_debits,
      totalCredits: total_credits,
      totalDebiteur: total_debiteur,
      totalCrediteur: total_crediteur,
      equilibre,
      nombreComptes: nombre_comptes,
      dossierId: dossier_id,
    });
  };
  const handleExportExcel6 = () => {
    exportBalance6Excel({
      balance,
      totalOuvertureDebit: raw?.total_ouverture_debit || 0,
      totalOuvertureCredit: raw?.total_ouverture_credit || 0,
      totalDebits: total_debits,
      totalCredits: total_credits,
      totalCumulDebiteur: raw?.total_cumul_debiteur || 0,
      totalCumulCrediteur: raw?.total_cumul_crediteur || 0,
      dossierId: dossier_id,
      dateDebut: date_debut,
    });
  };
  const handleExportPdf = () => {
    exportBalancePdf({
      balance,
      totalDebits: total_debits,
      totalCredits: total_credits,
      totalDebiteur: total_debiteur,
      totalCrediteur: total_crediteur,
      equilibre,
      nombreComptes: nombre_comptes,
      dossierId: dossier_id,
    });
  };

  // Filtrage + tri numérique croissant garanti
  const balanceFiltree = useMemo(() => {
    return balance
      .filter((e) => {
        const matchSearch = !search.trim() || (
          e.libelle?.toLowerCase().includes(search.toLowerCase()) ||
          String(e.numero_comptable).includes(search)
        );
        const matchClasse = !filterClasse || String(e.numero_comptable)[0] === filterClasse;
        const nature = e.nature_solde ?? (e.solde_debiteur > 0 ? 'D' : e.solde_crediteur > 0 ? 'C' : 'NUL');
        const matchSolde  = !filterSolde  || nature === filterSolde;
        return matchSearch && matchClasse && matchSolde;
      })
      .sort((a, b) => comparerComptes(a.numero_comptable, b.numero_comptable));
  }, [balance, search, filterClasse, filterSolde]);

  // Groupement par classe
  const groupes = useMemo(() => {
    if (!groupParClasse) return null;
    const map = {};
    balanceFiltree.forEach((e) => {
      const cls = String(e.numero_comptable)[0];
      if (!map[cls]) map[cls] = [];
      map[cls].push(e);
    });
    return map;
  }, [balanceFiltree, groupParClasse]);

  // Classes disponibles
  const classesDisponibles = useMemo(() => {
    const s = new Set(balance.map((e) => String(e.numero_comptable)[0]).filter(Boolean));
    return Array.from(s).sort();
  }, [balance]);

  // Sous-totaux par classe (pour affichage)
  const sousTotal = useMemo(() => {
    const map = {};
    balanceFiltree.forEach((e) => {
      const cls = String(e.numero_comptable)[0];
      if (!map[cls]) map[cls] = { debit:0, credit:0, sd:0, sc:0 };
      map[cls].debit  += e.debit           || 0;
      map[cls].credit += e.credit          || 0;
      map[cls].sd     += e.solde_debiteur  || 0;
      map[cls].sc     += e.solde_crediteur || 0;
    });
    return map;
  }, [balanceFiltree]);

  const hasFilters = search || filterClasse || filterSolde;
  const equilibreD = Math.abs(total_debits   - total_credits)   < 0.01;
  const equilibreS = Math.abs(total_debiteur - total_crediteur) < 0.01;

  if (isLoading || isFetching) {
    return (
      <div style={S.wrapper}>
        <div style={S.inner}>
          <div style={{ ...S.card, padding: '30px' }}>
            <TableLoader
              message="Calcul de la balance générale..."
              subMessage="Agrégation des débits/crédits et vérification de l'équilibre"
              minHeight="350px"
            />
          </div>
        </div>
      </div>
    );
  }

  // Rendu des lignes (groupé ou flat)
  const renderLignes = () => {
    if (!groupParClasse || !groupes) {
      return balanceFiltree.map((e, i) => (
        <LigneCompte key={e.numero_comptable} ecriture={e} index={i} />
      ));
    }

    const rows = [];
    Object.keys(groupes).sort().forEach((cls) => {
      const comptes = groupes[cls];
      const st      = sousTotal[cls] || {};

      // Ligne de classe
      rows.push(
        <tr key={'cls-' + cls} style={S.classeRow}>
          <td colSpan={7} style={S.classeTd}>
            {'▸  ' + (CLASSE_LABELS[cls] || ('Classe ' + cls))}
            <span style={{ marginLeft:'12px', fontWeight:400, opacity:0.7 }}>
              ({comptes.length} compte{comptes.length > 1 ? 's' : ''})
            </span>
          </td>
        </tr>
      );

      // Lignes de la classe
      comptes.forEach((e, i) => {
        rows.push(<LigneCompte key={e.numero_comptable} ecriture={e} index={i} />);
      });

      // Sous-total de classe
      rows.push(
        <tr key={'st-' + cls} style={{ background:'rgba(0,143,160,0.04)', borderTop:'1px solid rgba(0,143,160,0.15)' }}>
          <td colSpan={2} style={{ ...S.TD({ textAlign:'left', color:C.textDim, fontSize:'9px', letterSpacing:'1px', textTransform:'uppercase', fontFamily:C.mono }), borderRight:'2px solid '+C.border }}>
            Sous-total Classe {cls}
          </td>
          <td style={S.TD()}><span style={{ ...S.amtD, fontSize:'11px' }}>{fmt(st.debit)}</span></td>
          <td style={S.TD(S.tdGroupSep)}><span style={{ ...S.amtC, fontSize:'11px' }}>{fmt(st.credit)}</span></td>
          <td style={S.TD()}><span style={{ ...S.amtSolD, fontSize:'11px' }}>{fmt(st.sd)}</span></td>
          <td style={S.TD(S.tdGroupSep)}><span style={{ ...S.amtSolC, fontSize:'11px' }}>{fmt(st.sc)}</span></td>
          <td style={S.TD()} />
        </tr>
      );
    });

    return rows;
  };

  return (
    <div style={S.wrapper}>
      <div style={S.inner}>

        {/* ── Titre ── */}
        <div style={S.headerRow} className="compta-header-row">
          <div className="compta-title-wrap" style={{ display: 'flex', alignItems: 'center', gap: '14px', flexWrap: 'wrap' }}>
            <h2 style={S.title}>Balance Générale</h2>
            <span style={S.badge}>SYSCOHADA</span>
          </div>
          <div style={exportButtonGroupStyle} className="compta-export-group">
            <button type="button" onClick={handleExportExcel} style={exportExcelButtonStyle}>
              Export Excel (4 col.)
            </button>
            <button
              type="button"
              onClick={handleExportExcel6}
              style={exportExcelButtonStyle}
              title="Balance 6 colonnes : solde d'ouverture + mouvements + soldes cumulés"
            >
              Export Excel (6 col.)
            </button>
            <button type="button" onClick={handleExportPdf} style={exportPdfButtonStyle}>
              Export PDF
            </button>
          </div>
        </div>
        <div style={S.divider} />

        {/* ── Indicateurs d'équilibre ── */}
        <div style={S.indicators}>
          <div style={S.indicator(equilibreD)}>
            <span>{equilibreD ? '✓' : '✗'}</span>
            Équilibre mouvements
            <span style={{ fontWeight:400, opacity:0.7, marginLeft:'4px', fontSize:'10px' }}>
              D {fmt(total_debits)} = C {fmt(total_credits)}
            </span>
          </div>
          <div style={S.indicator(equilibreS)}>
            <span>{equilibreS ? '✓' : '✗'}</span>
            Équilibre soldes
            <span style={{ fontWeight:400, opacity:0.7, marginLeft:'4px', fontSize:'10px' }}>
              D {fmt(total_debiteur)} = C {fmt(total_crediteur)}
            </span>
          </div>
          <div style={{ fontFamily:C.mono, fontSize:'11px', color:C.textMuted, alignSelf:'center', marginLeft:'auto' }}>
            {nombre_comptes} compte{nombre_comptes > 1 ? 's' : ''}
          </div>
        </div>

        {/* ── Barre de filtres ── */}
        <div style={S.filterBar}>
          <span style={S.filterLabel}>Recherche</span>
          <input
            style={S.filterInput}
            placeholder="N° ou intitulé..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />

          <span style={S.filterLabel}>Classe</span>
          <select style={S.filterSelect} value={filterClasse} onChange={(e) => setFilterClasse(e.target.value)}>
            <option value="">Toutes</option>
            {classesDisponibles.map((c) => <option key={c} value={c}>Classe {c}</option>)}
          </select>

          <span style={S.filterLabel}>Solde</span>
          <select style={S.filterSelect} value={filterSolde} onChange={(e) => setFilterSolde(e.target.value)}>
            <option value="">Tous</option>
            <option value="D">Débiteur</option>
            <option value="C">Créditeur</option>
            <option value="NUL">Soldé (nul)</option>
          </select>

          {/* Toggle groupement */}
          <label style={{ display:'flex', alignItems:'center', gap:'6px', fontFamily:C.mono, fontSize:'10px', color:C.textDim, cursor:'pointer', userSelect:'none' }}>
            <input
              type="checkbox"
              checked={groupParClasse}
              onChange={(e) => setGroupParClasse(e.target.checked)}
              style={{ accentColor:C.teal, width:'14px', height:'14px' }}
            />
            Grouper par classe
          </label>

          <span style={S.countBadge}>
            {balanceFiltree.length} compte{balanceFiltree.length > 1 ? 's' : ''}
            {hasFilters ? ' / ' + balance.length : ''}
          </span>

          {hasFilters && (
            <button style={S.resetBtn} onClick={() => { setSearch(''); setFilterClasse(''); setFilterSolde(''); }}>
              Réinitialiser
            </button>
          )}
        </div>

        {/* ── Tableau ── */}
        <div style={S.card}>
          <div style={S.tableWrap}>
          <table style={S.table}>
            <thead>
              {/* Ligne 1 : groupes */}
              <tr>
                <th rowSpan={2} style={{ ...thLeft, verticalAlign:'middle', borderRight:'1px solid rgba(255,255,255,0.15)', minWidth:'90px', fontSize:'10px' }}>
                  N° Compte
                </th>
                <th rowSpan={2} style={{ ...thLeft, borderRight:'2px solid rgba(255,255,255,0.3)', minWidth:'180px', fontSize:'10px' }}>
                  Libellé
                </th>
                <th colSpan={2} style={{ ...thGroup, textAlign:'center' }}>
                  Mouvements
                </th>
                <th colSpan={2} style={{ ...thGroup, textAlign:'center' }}>
                  Soldes Finaux
                </th>
                <th rowSpan={2} style={{ ...thBase, fontSize:'10px', textAlign:'center', borderRight:'none', minWidth:'60px' }}>
                  Nature
                </th>
              </tr>
              {/* Ligne 2 : D / C */}
              <tr>
                <th style={{ ...thSub, fontSize:'11px' }}>Débit</th>
                <th style={{ ...thSub, fontSize:'11px', borderRight:'2px solid rgba(255,255,255,0.3)' }}>Crédit</th>
                <th style={{ ...thSub, fontSize:'11px', color:'rgba(9, 143, 87, 0.9)' }}>Débiteur</th>
                <th style={{ ...thSub, fontSize:'11px', color:'rgba(9, 143, 87, 0.9)', borderRight:'2px solid rgba(255,255,255,0.3)' }}>Créditeur</th>
              </tr>
            </thead>

            <tbody>
              {balanceFiltree.length === 0 ? (
                <tr><td colSpan={7} style={S.empty}>AUCUN COMPTE TROUVÉ</td></tr>
              ) : (
                renderLignes()
              )}

              {/* ── Ligne TOTAUX GÉNÉRAUX ── */}
              {balance.length > 0 && (
                <tr style={S.totalRow}>
                  <td colSpan={2} style={S.totalTd({ textAlign:'left', padding:'11px 12px', fontSize:'11px', letterSpacing:'1px' })}>
                    TOTAUX GÉNÉRAUX
                  </td>
                  <td style={S.totalTd({ color:C.green })}>
                    {fmt(total_debits)}
                  </td>
                  <td style={{ ...S.totalTd({ color:C.red }), borderRight:'2px solid rgba(0,143,160,0.3)' }}>
                    {fmt(total_credits)}
                  </td>
                  <td style={S.totalTd({ color:C.green })}>
                    {fmt(total_debiteur)}
                  </td>
                  <td style={{ ...S.totalTd({ color:C.red }), borderRight:'2px solid rgba(0,143,160,0.3)' }}>
                    {fmt(total_crediteur)}
                  </td>
                  <td style={S.totalTd({ textAlign:'center' })}>
                    {equilibre
                      ? <span style={{ color:C.green, fontSize:'14px' }}>✓</span>
                      : <span style={{ color:C.red,   fontSize:'14px' }}>✗</span>
                    }
                  </td>
                </tr>
              )}
            </tbody>
          </table>
          </div>
        </div>

        {/* ── Note de bas de page ── */}
        <div style={{ marginTop:'16px', fontFamily:C.mono, fontSize:'10px', color:C.textMuted, textAlign:'right', letterSpacing:'1px' }}>
          Règle : Σ Mouvements Débit = Σ Mouvements Crédit &nbsp;|&nbsp; Σ Soldes Débiteurs = Σ Soldes Créditeurs
        </div>

      </div>
    </div>
  );
};

export default Balance;